import { Story, StoryStatus, StoryType, Language, User, TerminologyItem, AuditLog } from '../types';
import { supabase } from './supabaseClient';

// --- Stories ---

export async function getStories(includeDeleted = false): Promise<Story[]> {
  let query = supabase
    .from('stories')
    .select('*')
    .order('updated_at', { ascending: false });

  if (!includeDeleted) {
    query = query.neq('status', 'deleted');
  }

  const { data, error } = await query;
  if (error) {
    console.error("Error fetching stories:", error);
    return [];
  }

  return data.map(mapStoryFromDB);
}

export async function getStory(id: string): Promise<Story | undefined> {
  const { data, error } = await supabase
    .from('stories')
    .select('*')
    .eq('id', id)
    .single();

  if (error || !data) return undefined;
  return mapStoryFromDB(data);
}

export async function saveStory(story: Story): Promise<void> {
  const dbStory = mapStoryToDB(story);
  
  // Check if update or insert
  const { error } = await supabase
    .from('stories')
    .upsert(dbStory);

  if (error) {
    console.error("Error saving story:", error);
    throw error;
  }

  // Basic logging
  if (story.status === StoryStatus.PUBLISHED) {
    logAction(story.authorId, "Published Story", `ID: ${story.id}`);
  }
}

export async function deleteStory(id: string, userId: string): Promise<void> {
  // Soft delete
  const { error } = await supabase
    .from('stories')
    .update({ status: StoryStatus.DELETED })
    .eq('id', id);

  if (!error) {
    logAction(userId, "Deleted Story", `ID: ${id}`);
  }
}

export function createEmptyStory(authorId: string): Story {
  return {
    id: crypto.randomUUID(), // Temp ID, will be overwritten by DB or used as UUID
    groupId: crypto.randomUUID(),
    headline: '',
    deck: '',
    body: '',
    language: Language.ENGLISH,
    type: StoryType.BREAKING,
    status: StoryStatus.DRAFT,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    authorId,
    editorIssues: []
  };
}

export async function createStoryFromTranslation(original: Story, translatedContent: { headline: string, deck: string, body: string }, targetLang: Language): Promise<Story> {
  const newStory = createEmptyStory(original.authorId);
  newStory.groupId = original.groupId;
  newStory.headline = translatedContent.headline;
  newStory.deck = translatedContent.deck;
  newStory.body = translatedContent.body;
  newStory.language = targetLang;
  newStory.originalBody = translatedContent.body;
  newStory.originalHeadline = translatedContent.headline;
  newStory.originalDeck = translatedContent.deck;
  newStory.isTranslation = true;
  newStory.status = StoryStatus.DRAFT; 
  
  await saveStory(newStory);
  return newStory;
}

// --- Terminology ---

export async function getTerminology(language?: Language): Promise<TerminologyItem[]> {
  let query = supabase.from('terminology').select('*');
  
  if (language) {
    query = query.eq('language', language);
  }

  const { data, error } = await query;
  if (error) return [];

  return data.map((t: any) => ({
    id: t.id,
    original: t.original,
    replacement: t.replacement,
    language: t.language as Language,
    type: t.type,
    context: t.context,
    createdBy: t.created_by,
    createdAt: t.created_at
  }));
}

export async function saveTerminology(term: TerminologyItem, userId: string): Promise<void> {
  // Check for duplicates handled by SQL constraint, but we catch it here
  const { error } = await supabase
    .from('terminology')
    .upsert({
      id: term.id.length < 10 ? undefined : term.id, // Handle temp IDs
      original: term.original,
      replacement: term.replacement,
      language: term.language,
      type: term.type,
      context: term.context,
      created_by: userId
    });

  if (error) {
    if (error.code === '23505') { // Unique violation code for Postgres
       throw new Error(`The term "${term.original}" already exists in ${term.language}.`);
    }
    throw error;
  }
  
  await logAction(userId, "Updated Terminology", `${term.original} -> ${term.replacement}`);
}

export async function deleteTerminology(id: string, userId: string): Promise<void> {
  await supabase.from('terminology').delete().eq('id', id);
  await logAction(userId, "Deleted Terminology", `ID: ${id}`);
}

/**
 * Helper to generate the glossary string for the AI prompt
 */
export async function getTerminologyPrompt(language: Language): Promise<string> {
  const terms = await getTerminology(language);
  if (terms.length === 0) return "";

  return `
    STRICT TERMINOLOGY GLOSSARY (You MUST use the Replacement term):
    ${terms.map(t => `- "${t.original}" -> REPLACE WITH "${t.replacement}" (${t.context || ''})`).join('\n')}
  `;
}

// --- Users & Logs ---

export async function getUsers(): Promise<User[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*');
    
  if (error) return [];
  
  return data.map((u: any) => ({
    id: u.id,
    name: u.full_name || u.email,
    email: u.email,
    role: u.role,
    avatar: u.avatar_url,
    lastLogin: u.last_login
  }));
}

export async function updateUserRole(targetUserId: string, newRole: string): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({ role: newRole })
    .eq('id', targetUserId);

  if (error) throw error;
  
  // Log this action
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    await logAction(user.id, "Updated User Role", `Changed user ${targetUserId} to ${newRole}`);
  }
}

export async function deleteUser(targetUserId: string): Promise<void> {
  // Soft delete by setting role to 'suspended'
  const { error } = await supabase
    .from('profiles')
    .update({ role: 'suspended' })
    .eq('id', targetUserId);

  if (error) throw error;
  
  // Log this action
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    await logAction(user.id, "Suspended User", `Soft deleted user ${targetUserId}`);
  }
}

export async function getLogs(): Promise<AuditLog[]> {
  // Join with profiles table to get actual user names
  const { data, error } = await supabase
    .from('audit_logs')
    .select(`
      *,
      profiles:user_id (full_name, email)
    `)
    .order('timestamp', { ascending: false })
    .limit(100);

  if (error) {
    console.error("Error fetching logs:", error);
    return [];
  }

  return data.map((l: any) => ({
    id: l.id,
    userId: l.user_id,
    // Use joined profile data, with fallbacks
    userName: l.profiles?.full_name || l.profiles?.email || l.user_name || 'System',
    action: l.action,
    details: l.details,
    timestamp: l.timestamp
  }));
}

export async function logAction(userId: string, action: string, details?: string): Promise<void> {
  // Fetch user name for the log cache
  const { data: user } = await supabase.from('profiles').select('full_name').eq('id', userId).single();
  
  await supabase.from('audit_logs').insert({
    user_id: userId,
    user_name: user?.full_name || 'System',
    action,
    details
  });
}

// --- Mappers ---

function mapStoryFromDB(db: any): Story {
  return {
    id: db.id,
    groupId: db.group_id,
    isTranslation: db.is_translation,
    headline: db.headline,
    deck: db.deck,
    body: db.body,
    language: db.language as Language,
    type: db.type as StoryType,
    status: db.status as StoryStatus,
    createdAt: db.created_at,
    updatedAt: db.updated_at,
    authorId: db.author_id,
    originalHeadline: db.original_headline,
    originalDeck: db.original_deck,
    originalBody: db.original_body,
    // Ensure arrays are initialized to prevent crashes if DB has null
    editorIssues: db.editor_issues || [],
    socialPosts: db.social_posts || undefined
  };
}

function mapStoryToDB(story: Story): any {
  return {
    id: story.id,
    group_id: story.groupId,
    is_translation: story.isTranslation,
    headline: story.headline,
    deck: story.deck,
    body: story.body,
    language: story.language,
    type: story.type,
    status: story.status,
    author_id: story.authorId,
    original_headline: story.originalHeadline,
    original_deck: story.originalDeck,
    original_body: story.originalBody,
    editor_issues: story.editorIssues,
    social_posts: story.socialPosts,
    updated_at: new Date().toISOString()
  };
}